/**
 * T.10 S18 (D130, review H1). CPD's education level (`api.educationlevel`
 * ids, as education-wording.ts words them) for an education line read from
 * an uploaded resume, by a fixed rule on its words. Doubtful is wrong: text
 * no rule names, or a degree not finished (coursework, toward, pursuing, in
 * progress, expected), gets no level, and the wizard asks the member.
 * Not the builder's `educationLevelFor`, which reads a different shape.
 */

/** Ids of `api.educationlevel`. */
export const EDUCATION_LEVEL = {
  highSchool: 2,
  certificate: 3,
  associate: 5,
  bachelor: 6,
  graduateCertificate: 7,
  master: 8,
  postMasterCertificate: 9,
  professional: 10,
  doctorate: 11,
  ccafDegree: 14
} as const;

/** A degree not (yet) held: the member is asked. */
const UNFINISHED = /\b(?:coursework|course work|toward|towards|pursuing|in progress|expected|anticipated|candidate|currently enrolled|incomplete)\b/i;

/** Commas, brackets and slashes read as spaces; periods kept, so "M.S." and "MS" can be told apart. */
const spaced = (text: string) => ` ${text.replace(/[,;:()/]/g, ' ').replace(/\s+/g, ' ').trim()} `;

/** A word or phrase, any case, as whole words. */
const word = (src: string) => new RegExp(`\\s(?:${src})(?=\\s)`, 'i');
/** Abbreviations that are only ever a degree, with or without periods ("BS", "B.S."). */
const abbr = (src: string) => new RegExp(`\\s(?:${src})(?=\\s)`);
/** MS, MA, MD and DBA are also software, a medical assistant and a database job: only with their periods, or before "in <program>". */
const strict = (dotted: string, bare: string) => new RegExp(`\\s(?:${dotted}|(?:${bare})(?= in \\S))(?=\\s)`);

/** "degree context": of, in, degree, or 's / s. */
const CONTEXT = `(?:'s|s)(?: degree)?|(?:'s|s)? (?:of|in|degree)`;

/** In order: the first rule that names the text wins. */
const RULES: { level: number; test: RegExp[] }[] = [
  { level: EDUCATION_LEVEL.postMasterCertificate, test: [word(`post-?master(?:'s|s)? certificate`)] },
  { level: EDUCATION_LEVEL.professional, test: [word('juris doctor'), abbr('J\\.D\\.|JD|PharmD|Pharm\\.D\\.|DDS|D\\.D\\.S\\.|DVM|D\\.V\\.M\\.'), strict('M\\.D\\.', 'MD')] },
  { level: EDUCATION_LEVEL.doctorate, test: [word('doctor of [a-z]+|doctorate|doctoral degree'), abbr('PhD|Ph\\.D\\.|EdD|Ed\\.D\\.|DNP|PsyD|Psy\\.D\\.'), strict('D\\.B\\.A\\.', 'DBA')] },
  { level: EDUCATION_LEVEL.graduateCertificate, test: [word('graduate certificate|post-?baccalaureate certificate')] },
  { level: EDUCATION_LEVEL.master, test: [word(`master(?:${CONTEXT})`), abbr('MBA|M\\.B\\.A\\.|MEd|M\\.Ed\\.|MPA|M\\.P\\.A\\.|MSc|M\\.Sc\\.|MSN|MPH|MSW|MFA|MEng'), strict('M\\.S\\.|M\\.A\\.', 'MS|MA')] },
  { level: EDUCATION_LEVEL.bachelor, test: [word(`bachelor(?:${CONTEXT})|baccalaureate`), abbr('BS|B\\.S\\.|BA|B\\.A\\.|BSc|B\\.Sc\\.|BBA|B\\.B\\.A\\.|BFA|BSN|BEng|BAS')] },
  { level: EDUCATION_LEVEL.associate, test: [word(`associate(?:${CONTEXT})`), abbr('AA|A\\.A\\.|AS|A\\.S\\.|AAS|A\\.A\\.S\\.|ASN')] },
  { level: EDUCATION_LEVEL.certificate, test: [word('certificate')] },
  { level: EDUCATION_LEVEL.highSchool, test: [word('high school|ged|general educational development|high school equivalency')] }
];

const CCAF = /community college of the air force|\bCCAF\b/i;

/** The level the credential names, or null when no rule names one or it is not finished. */
export function educationLevelFromCredential(credential: string | null | undefined, institution?: string | null): number | null {
  const raw = String(credential ?? '').trim();
  if (!raw || UNFINISHED.test(raw)) {
    return null;
  }
  const t = spaced(raw);
  // The Community College of the Air Force's associate degree, named in the line or as the school.
  if (CCAF.test(`${raw} ${institution ?? ''}`) && (/\bdegree\b/i.test(raw) || RULES.find((r) => r.level === EDUCATION_LEVEL.associate)?.test.some((re) => re.test(t)))) {
    return EDUCATION_LEVEL.ccafDegree;
  }
  return RULES.find((r) => r.test.some((re) => re.test(t)))?.level ?? null;
}

/** The degree words at the start of a credential, spelled out or as an abbreviation. */
const LEAD_WORDS =
  /^(?:(?:bachelor|master|associate)(?:'s|s)?(?: degree)?(?: of (?:applied )?(?:science|arts|fine arts))?|doctor of philosophy|doctorate|juris doctor|(?:graduate )?certificate|high school(?: diploma)?|ged)/i;
const LEAD_ABBR = /^[A-Z]\.?(?:[A-Za-z]\.?){1,3}(?=[\s,:]|$)/;

/** What was studied, from the credential ("B.S. Business Administration" to "Business Administration"); '' when it names none. */
export function programFromCredential(text: string | null | undefined): string {
  const t = String(text ?? '').replace(/\s+/g, ' ').trim();
  if (!t || educationLevelFromCredential(t) === null) {
    return '';
  }
  const lead = LEAD_WORDS.exec(t) ?? LEAD_ABBR.exec(t);
  const rest = (lead ? t.slice(lead[0].length) : t)
    .replace(/^[\s,:;-]+/, '')
    .replace(/^(?:in|of)\s+/i, '')
    .replace(/[,\s]+$/, '')
    .trim();
  // "Diploma", "Degree" and "(GED)" left over are not a program.
  return /^(?:diploma|degree|\(?ged\)?|general educational development.*)?$/i.test(rest) ? '' : rest;
}
